import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  getPages,
  getPageStats,
  Page,
  PAGE_LANGUAGES,
} from '@/lib/pages-service';
import PageEditorDialog from '@/components/admin/pages/PageEditorDialog';
import SEO from '@/components/SEO';
import {
  Search,
  Layout,
  FileEdit,
  Globe,
  Layers,
  Edit,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Lock,
  Unlock,
  Loader2,
} from 'lucide-react';

const AdminPages = () => {
  const { toast } = useToast();
  const { role } = useAuth();

  // Permissions
  const canEdit = role === 'superadmin' || role === 'admin' || role === 'editor';

  // State
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, english: 0, spanish: 0, editable: 0 });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Dialog
  const [showEditorDialog, setShowEditorDialog] = useState(false);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);

  const fetchPages = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getPages({
        search: searchTerm,
        language: languageFilter,
        page,
        pageSize,
      });

      setPages(result.data);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (error: any) {
      console.error('Error fetching pages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, languageFilter, page, pageSize, toast]);

  const fetchStats = async () => {
    try {
      const statsData = await getPageStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchPages();
    fetchStats();
  }, [fetchPages]);

  const handleEdit = (pageItem: Page) => {
    if (!pageItem.is_editable) {
      toast({
        title: 'Not Editable',
        description: 'This page is locked and cannot be edited.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedPage(pageItem);
    setShowEditorDialog(true);
  };

  const handleRefresh = () => {
    fetchPages();
    fetchStats();
  };

  const getLanguageBadge = (language: string) => {
    const lang = PAGE_LANGUAGES.find(l => l.value === language);
    const colorMap: Record<string, string> = {
      en: 'bg-blue-100 text-blue-800',
      es: 'bg-orange-100 text-orange-800',
      vi: 'bg-green-100 text-green-800',
    };
    return (
      <Badge className={colorMap[language] || ''}>
        {lang?.label || language.toUpperCase()}
      </Badge>
    );
  };

  return (
    <>
      <SEO
        title="Pages Management - Admin"
        description="Manage website pages and content"
      />
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Pages / Content Management</h1>
            <p className="text-muted-foreground">
              Edit website content and SEO metadata
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Pages</CardTitle>
              <Layout className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">English</CardTitle>
              <Globe className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.english}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Español</CardTitle>
              <Globe className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.spanish}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Editable</CardTitle>
              <Unlock className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.editable}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <CardTitle>All Pages</CardTitle>
                <CardDescription>{total} pages found</CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or slug..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  className="pl-9"
                />
              </div>

              <Select value={languageFilter} onValueChange={(value) => { setLanguageFilter(value); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  {PAGE_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="mt-2 text-sm text-muted-foreground">Loading pages...</p>
              </div>
            ) : pages.length === 0 ? (
              <div className="text-center py-12">
                <Layers className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No pages found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Page Name</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Language</TableHead>
                        <TableHead>Editable</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pages.map((pageItem) => (
                        <TableRow key={pageItem.id}>
                          <TableCell>
                            <div className="font-medium">{pageItem.name}</div>
                            {pageItem.meta_title && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {pageItem.meta_title}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <code className="text-sm bg-muted px-2 py-1 rounded">
                              {pageItem.slug}
                            </code>
                          </TableCell>
                          <TableCell>{getLanguageBadge(pageItem.language)}</TableCell>
                          <TableCell>
                            {pageItem.is_editable ? (
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                <Unlock className="h-3 w-3 mr-1" />
                                Yes
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">
                                <Lock className="h-3 w-3 mr-1" />
                                No
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {format(new Date(pageItem.updated_at), 'MMM dd, yyyy')}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(pageItem)}
                                disabled={!canEdit || !pageItem.is_editable}
                                title={pageItem.is_editable ? 'Edit page' : 'Page is locked'}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages} ({total} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Editor Dialog */}
      <PageEditorDialog
        page={selectedPage}
        open={showEditorDialog}
        onOpenChange={setShowEditorDialog}
        onPageSaved={() => {
          fetchPages();
          fetchStats();
        }}
      />
    </>
  );
};

export default AdminPages;